import { addDays } from '../analyticsUi';
import { buildBudgetAdvice } from '../budgetModel';
import { eur, eurRange, ageLabel, horizonLabel, MIN_TABLE_USERS } from './profitFormat';

// A projection step needs at least this many users who actually lived through it
const MIN_STEP_USERS = 30;
// Days since signup after which a user counts half as much in the projection
const RECENCY_HALF_LIFE = 180;
const BOOTSTRAP_RUNS = 200;
const FORECAST_MONTHS = 6;

function sumRange(daily, from, to) {
  const t = { spend: 0, subRevenue: 0, adRevenue: 0, installs: 0, installsPaid: 0, signups: 0, trialsStarted: 0, trialsConverted: 0 };
  daily.forEach(d => {
    if (d.date < from || d.date > to) return;
    Object.keys(t).forEach(k => { t[k] += d[k] || 0; });
  });
  t.revenue = t.subRevenue + t.adRevenue;
  t.profit = t.revenue - t.spend;
  t.costPerUser = t.signups ? t.spend / t.signups : null;
  return t;
}

// Extends the steps to 2.5 years where no signup month is old enough yet (far enough to include the
// second yearly renewal); the table and verdict stop
// at 1 year, only the chart goes further. Earnings per day slow down with age, so it fits an
// exponential decay to the daily rate of the last three steps (weighted by users) and adds up that
// curve. Only the tail is used: the steep early drop-off (trials ending, casual players leaving) is
// over by then and would make the decay far too fast. If earnings aren't slowing, the last rate is
// held flat. Yearly renewals are added separately in buildProjection.
function extrapolateSteps(steps) {
  const last = steps[steps.length - 1];
  const points = steps
    .filter(s => s.from >= 30 && s.perUser > 0)
    .slice(-3)
    .map(s => ({ t: (s.from + s.to) / 2, lnRate: Math.log(s.perUser / (s.to - s.from)), w: s.users }));
  if (!last || last.to >= 912 || points.length < 2) return [];

  const W = points.reduce((s, p) => s + p.w, 0);
  const tMean = points.reduce((s, p) => s + p.w * p.t, 0) / W;
  const yMean = points.reduce((s, p) => s + p.w * p.lnRate, 0) / W;
  const slope = points.reduce((s, p) => s + p.w * (p.t - tMean) * (p.lnRate - yMean), 0)
    / points.reduce((s, p) => s + p.w * (p.t - tMean) ** 2, 0);
  const decay = Math.max(0, -slope);
  const lastRate = last.perUser / (last.to - last.from);
  const rate = decay > 0 ? (t) => Math.exp(yMean + slope * (t - tMean)) : () => lastRate;
  const earnedBetween = (a, b) => (decay > 0 ? (rate(a) - rate(b)) / decay : lastRate * (b - a));

  const out = [];
  let from = last.to;
  [270, 365, 395, 540, 912].filter(age => age > from).forEach(to => {
    out.push({ from, to, perUser: earnedBetween(from, to), extrapolated: true });
    from = to;
  });
  return out;
}

// Signup days: per-user cumulative earnings at each age the day's users have lived through, and a
// weight that halves every RECENCY_HALF_LIFE days since signup, so app and pricing changes show up
// sooner. (A backtest on past months found no accuracy lost to a half-life this long.)
function prepareDays(signupDays, ages, today) {
  return signupDays.filter(d => d.users > 0).map(d => ({
    date: d.date,
    users: d.users,
    weight: d.users * 0.5 ** (Math.max(0, (new Date(today) - new Date(d.date)) / 86400000) / RECENCY_HALF_LIFE),
    ages: ages.slice(0, d.sub.length),
    value: d.sub.map((s, i) => (s + d.ad[i]) / d.users),
    ad: d.ad.map(a => a / d.users),
  }));
}

// Projects earnings per user from how older signups grew. For each step between two ages
// (e.g. day 30 -> day 90) it takes the weighted average of what every signup day that lived through
// that step earned in it. Adding the same amount (rather than scaling a day's earnings so far)
// predicted past months better. A typical user is the day-1 average plus every step; a signup day is
// its own numbers so far plus the steps it hasn't reached yet. Steps with too few users end it.
function buildProjection(days, renewal) {
  const ages = days.reduce((longest, d) => (d.ages.length > longest.length ? d.ages : longest), []);

  const steps = [];
  for (let i = 0; i + 1 < ages.length; i++) {
    let earned = 0;
    let weight = 0;
    let users = 0;
    days.forEach(d => {
      if (d.value.length <= i + 1) return;
      earned += (d.value[i + 1] - d.value[i]) * d.weight;
      weight += d.weight;
      users += d.users;
    });
    if (users < MIN_STEP_USERS) break;
    steps.push({ from: ages[i], to: ages[i + 1], perUser: earned / weight, users });
  }
  steps.push(...extrapolateSteps(steps));

  const typical = [];
  const day1 = days.filter(d => d.value.length);
  if (day1.reduce((s, d) => s + d.users, 0) >= MIN_STEP_USERS) {
    let value = day1.reduce((s, d) => s + d.value[0] * d.weight, 0) / day1.reduce((s, d) => s + d.weight, 0);
    typical.push({ age: 0, value });
    steps.forEach(s => {
      value += s.perUser;
      typical.push({ age: s.to, value, extrapolated: !!s.extrapolated });
    });
  }

  // Yearly-plan renewals are a lump the smooth extrapolation can't see. Add the expected first
  // renewal from its age on, and a second one a year later at the same rate (cautious: people who
  // renewed once are more likely to renew again). Only while no real data reaches the first renewal
  // age, since that data would already include them.
  const lastObserved = [...typical].reverse().find(p => !p.extrapolated);
  const renewalAdded = !!(renewal?.perUser && lastObserved && lastObserved.age < renewal.age);
  const expectedRenewals = (age) => {
    if (!renewalAdded) return 0;
    return (age >= renewal.age ? renewal.perUser : 0) + (age >= renewal.age + 365 ? renewal.perUser * renewal.rate : 0);
  };
  typical.forEach(p => {
    if (p.extrapolated) p.value += expectedRenewals(p.age);
  });

  // One signup day: age -> { value, ad (observed only), projected }, with the same expected
  // renewals as the typical user
  const projectDay = (d) => {
    const out = {};
    d.ages.forEach((age, i) => { out[age] = { value: d.value[i], ad: d.ad[i], projected: false }; });
    if (!d.ages.length) return out;
    let value = d.value[d.value.length - 1];
    steps.filter(s => s.from >= d.ages[d.ages.length - 1]).forEach(s => {
      value += s.perUser;
      out[s.to] = { value: value + (s.extrapolated ? expectedRenewals(s.to) : 0), projected: true };
    });
    return out;
  };
  // A group of signup days (a month): user-weighted average at every age all of its days reach
  const project = (group) => {
    const perDay = group.map(d => ({ users: d.users, cells: projectDay(d) }));
    const users = group.reduce((s, d) => s + d.users, 0);
    const out = {};
    if (!users) return out;
    Object.keys(perDay[0]?.cells || {}).forEach(age => {
      if (!perDay.every(p => p.cells[age])) return;
      const projected = perDay.some(p => p.cells[age].projected);
      out[age] = {
        value: perDay.reduce((s, p) => s + p.cells[age].value * p.users, 0) / users,
        ad: projected ? null : perDay.reduce((s, p) => s + p.cells[age].ad * p.users, 0) / users,
        projected,
      };
    });
    return out;
  };

  return { typical, project, renewalAdded };
}

// How far the typical user's value at `age` could be off just from which users happened to sign
// up: rebuilds the projection from signup days drawn at random (with replacement) and returns
// the middle 90% of results. Seeded so the range doesn't change between renders.
function bootstrapRange(days, renewal, age) {
  let seed = 12345;
  const random = () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const values = [];
  for (let run = 0; run < BOOTSTRAP_RUNS; run++) {
    const sample = days.map(() => days[Math.floor(random() * days.length)]);
    const point = buildProjection(sample, renewal).typical.find(p => p.age === age);
    if (point) values.push(point.value);
  }
  if (values.length < BOOTSTRAP_RUNS * 0.9) return null;
  values.sort((a, b) => a - b);
  return { low: values[Math.floor(values.length * 0.05)], high: values[Math.ceil(values.length * 0.95) - 1] };
}

const daysBetween = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
export const BACKTEST_AGE = 90;
const BACKTEST_LOOKBACKS = [45, 90, 135];

// Runs the projection as it would have looked some time ago (only data up to that day) and compares
// its guess for what signups of that time earn in their first 90 days with what they really earned.
function backtest(days, renewal, today) {
  const full = buildProjection(days, renewal);
  const rows = [];
  BACKTEST_LOOKBACKS.forEach(back => {
    const cutoff = addDays(today, -back);
    const lastSeen = addDays(cutoff, -1);
    const truncated = [];
    days.forEach(d => {
      if (d.date > lastSeen) return;
      const k = d.ages.filter(a => a <= daysBetween(d.date, lastSeen)).length;
      if (!k) return;
      truncated.push({
        ...d,
        weight: d.users * 0.5 ** (daysBetween(d.date, cutoff) / RECENCY_HALF_LIFE),
        ages: d.ages.slice(0, k),
        value: d.value.slice(0, k),
        ad: d.ad.slice(0, k),
      });
    });
    // Signups that hadn't reached 90 days at the cutoff but have by now
    const inGroup = (d) => d.date <= lastSeen && daysBetween(d.date, lastSeen) < BACKTEST_AGE;
    const groupNow = days.filter(d => inGroup(d) && d.ages.includes(BACKTEST_AGE));
    const keep = new Set(groupNow.map(d => d.date));
    const groupThen = truncated.filter(d => keep.has(d.date));
    if (!groupThen.length) return;
    const predicted = buildProjection(truncated, renewal).project(groupThen)[BACKTEST_AGE];
    const actual = full.project(groupNow)[BACKTEST_AGE];
    if (!predicted || !actual || actual.projected || !(actual.value > 0)) return;
    rows.push({
      cutoff,
      users: groupNow.reduce((s, d) => s + d.users, 0),
      predicted: predicted.value,
      actual: actual.value,
      error: (predicted.value - actual.value) / actual.value,
    });
  });
  if (!rows.length) return null;
  return { rows, meanAbsError: rows.reduce((s, r) => s + Math.abs(r.error), 0) / rows.length };
}

// Cumulative earnings per user at an age, from [{ age, value }] points sorted by age: linear
// between points, nothing before signup, flat after the last point (no earnings assumed beyond it)
function cumulativeAt(points, age) {
  if (!points.length || age < points[0].age) return 0;
  for (let i = 1; i < points.length; i++) {
    if (age <= points[i].age) {
      const [a, b] = [points[i - 1], points[i]];
      return a.value + ((age - a.age) / (b.age - a.age)) * (b.value - a.value);
    }
  }
  return points[points.length - 1].value;
}

function monthStartKey(today, offset) {
  const d = new Date(`${today.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + offset);
  return d.toISOString().slice(0, 10);
}

// Cash profit for each of the next calendar months. Existing users: every signup day's projected
// earnings curve, taken between the start and end of the month. New users: signups and ad spend
// continue at the current budget's pace (the last-30-day pace while it's too new to measure), each
// new user earning like the typical user. Without ads, only the existing users count (organic
// signups would add a little on top).
function buildCashForecast({ days, project, typical, last30, pace, today }) {
  if (typical.length < 2) return null;
  const curves = days.map(d => ({
    date: d.date,
    users: d.users,
    points: Object.entries(project([d])).map(([age, c]) => ({ age: Number(age), value: c.value })).sort((a, b) => a.age - b.age),
  }));
  const typicalPoints = typical.map(p => ({ age: p.age, value: p.value }));
  const signupsPerDay = pace ? pace.signupsPerDay : last30.signups / 30;
  const spendPerDay = pace ? pace.spendPerDay : last30.spend / 30;

  const rows = [];
  for (let k = 1; k <= FORECAST_MONTHS; k++) {
    const start = monthStartKey(today, k);
    const before = addDays(start, -1);
    const last = addDays(monthStartKey(today, k + 1), -1);
    const existing = curves.reduce(
      (sum, c) => sum + c.users * (cumulativeAt(c.points, daysBetween(c.date, last)) - cumulativeAt(c.points, daysBetween(c.date, before))),
      0,
    );
    let future = 0;
    for (let signup = addDays(today, 1); signup <= last; signup = addDays(signup, 1)) {
      future += signupsPerDay * (cumulativeAt(typicalPoints, daysBetween(signup, last)) - cumulativeAt(typicalPoints, daysBetween(signup, before)));
    }
    const spend = spendPerDay * (daysBetween(before, last));
    rows.push({ month: start.slice(0, 7), existing, future, spend, keep: existing + future - spend, stop: existing });
  }
  return { rows, signupsPerDay, spendPerDay, paceFrom: pace?.from ?? null };
}

// The headline answer: at what ads cost per user now, do users earn that back?
// Cost is a range: all new users (best case) vs only users Google credits to ads (worst case).
function buildVerdict({ last30, paidShare, typical, valueRange, hasSubData }) {
  if (!last30.spend) {
    return { status: 'info', title: 'No ad spend in the last 30 days', text: 'Everything earned in that time is organic.' };
  }
  if (!hasSubData) {
    return {
      status: 'info',
      title: 'Waiting for subscription data',
      text: "RevenueCat's purchase history hasn't arrived yet. Without it ads would look worse than they are, so there's no verdict yet.",
    };
  }
  if (!last30.signups || typical.length < 2) {
    return { status: 'info', title: 'Not enough history yet', text: 'A verdict needs a few months of users with their earnings.' };
  }

  const horizonPoint = [...typical].reverse().find(p => p.age <= 365);
  const value = horizonPoint.value;
  const horizon = horizonLabel(horizonPoint.age);
  const costLow = last30.costPerUser;
  const costHigh = paidShare > 0 && paidShare < 1 ? costLow / paidShare : null;
  const best = value / costLow;
  const worst = costHigh ? value / costHigh : best;
  const payback = typical.find(p => p.value >= costLow);
  const lastObserved = [...typical].reverse().find(p => !p.extrapolated);
  let shortHistory = '';
  if (horizonPoint.age < 365) {
    shortHistory = ` There isn't a full year of history yet, so later renewals would come on top.`;
  } else if (horizonPoint.extrapolated) {
    shortHistory = ` Beyond ${ageLabel(lastObserved.age)} it's extrapolated.`;
  }

  const base = `A typical user earns you ${eur(value, true)} in their ${horizon}. A new user costs ${eurRange(costLow, costHigh)} in ads, so each €1 spent comes back as ${eurRange(worst, best)}.`;
  let status;
  let title;
  let text;
  if (worst >= 1) {
    status = 'good';
    title = 'Ads are paying off';
    text = `${base}${shortHistory}`;
  } else if (best >= 1) {
    status = 'warn';
    title = 'Probably paying off';
    text = `${base} Only true if ads bring in more users than Google credits them for.${shortHistory}`;
  } else {
    status = 'bad';
    title = horizonPoint.age >= 365 ? 'Ads cost more than they earn back' : 'Ads haven’t paid back yet';
    text = `${base}${shortHistory}`;
  }

  return {
    status,
    title,
    text,
    figures: [
      { label: 'Ad cost per new user', value: eurRange(costLow, costHigh), note: costHigh ? 'All new users – only users credited to ads' : null },
      {
        label: `Earned per user, ${horizon}`,
        value: eur(value, true),
        note: [
          horizonPoint.extrapolated ? `Typical user; after ${ageLabel(lastObserved.age)} extrapolated` : 'Typical user, from past signups',
          valueRange ? `90% likely ${eurRange(valueRange.low, valueRange.high)}` : null,
        ].filter(Boolean).join('. '),
      },
      { label: 'Return per €1 spent', value: eurRange(worst, best), note: costHigh ? 'Only credited users – all new users' : null },
      { label: 'Pays back within', value: payback ? ageLabel(payback.age) : `Not in ${horizon}` },
    ],
  };
}

// Everything the Profit tab shows, computed from the cached analytics, the chosen range and the markers
export function buildView(data, rangeDays, markers) {
  if (!data?.daily?.length) return null;
  const daily = data.daily;
  const today = daily[daily.length - 1].date;
  const last30 = sumRange(daily, addDays(today, -29), today);
  const prev30 = sumRange(daily, addDays(today, -59), addDays(today, -30));
  const paidShare = last30.installs ? last30.installsPaid / last30.installs : null;

  const from = rangeDays ? addDays(today, -(rangeDays - 1)) : daily[0].date;
  const rangeDaily = daily
    .filter(d => d.date >= from)
    .map((d, i, arr) => {
      const window = arr.slice(Math.max(0, i - 6), i + 1);
      return {
        ...d,
        installsOther: d.installs - d.installsPaid,
        installsAvg: window.length === 7 ? window.reduce((s, w) => s + w.installs, 0) / 7 : null,
      };
    });

  const health = data.health || {};
  const cohorts = data.cohorts || [];
  const days = prepareDays(data.signupDays || [], data.cohortAges || [], today);
  const { typical, project, renewalAdded } = buildProjection(days, data.renewal);
  // Decision horizon: a year plus the first yearly renewal (about 13 months) when renewals are modelled,
  // otherwise the latest age within a year
  const renewalAge = data.renewal?.age;
  const returnAge = (renewalAge != null ? typical.find(p => p.age >= renewalAge && p.age <= 540)?.age : null)
    ?? [...typical].reverse().find(p => p.age <= 365)?.age;
  const cohortRows = [...cohorts].reverse().map(c => {
    const projected = project(days.filter(d => d.date.startsWith(c.month)));
    const atReturn = returnAge != null ? projected[returnAge] : null;
    const paybackAge = c.cac > 0
      ? Object.keys(projected).map(Number).sort((a, b) => a - b).find(a => projected[a].value >= c.cac)
      : null;
    return {
      ...c,
      projected,
      return: atReturn && c.cac > 0 ? { value: atReturn.value / c.cac, projected: atReturn.projected } : null,
      expectedProfit: atReturn ? c.users * atReturn.value - c.spend : null,
      payback: paybackAge != null ? { age: paybackAge, projected: projected[paybackAge].projected } : null,
    };
  });
  const expectedByMonth = {};
  cohortRows.forEach(c => {
    if (c.expectedProfit != null && c.users >= MIN_TABLE_USERS) expectedByMonth[c.month] = c.expectedProfit;
  });

  // Profit the spend of a window is expected to make once its new users have had time to earn
  const expectedFor = (fromKey, toKey, spend) => {
    const group = days.filter(d => d.date >= fromKey && d.date <= toKey);
    const users = group.reduce((s, d) => s + d.users, 0);
    const cell = returnAge != null && users ? project(group)[returnAge] : null;
    return cell ? { users, perUser: cell.value, spend, profit: users * cell.value - spend } : null;
  };
  const expectedNow = expectedFor(addDays(today, -29), today, last30.spend);
  const expectedPrev = expectedFor(addDays(today, -59), addDays(today, -30), prev30.spend);
  const valueRange = returnAge != null ? bootstrapRange(days, data.renewal, returnAge) : null;
  const typicalAtReturn = typical.find(p => p.age === returnAge);
  const expectedRange = expectedNow && valueRange && typicalAtReturn?.value > 0
    ? {
      low: expectedNow.users * expectedNow.perUser * (valueRange.low / typicalAtReturn.value) - expectedNow.spend,
      high: expectedNow.users * expectedNow.perUser * (valueRange.high / typicalAtReturn.value) - expectedNow.spend,
    }
    : null;
  const accuracy = backtest(days, data.renewal, today);
  const budget = buildBudgetAdvice({
    daily,
    adsBudget: data.adsBudget,
    markers,
    value: typicalAtReturn?.value,
    typical,
    costLow: last30.costPerUser,
    costHigh: last30.costPerUser && paidShare > 0 && paidShare < 1 ? last30.costPerUser / paidShare : null,
    today,
  });
  const forecast = buildCashForecast({ days, project, typical, last30, pace: budget?.pace, today });

  return {
    today,
    last30,
    prev30,
    paidShare,
    rangeDaily,
    typical,
    renewalAdded,
    returnAge,
    cohortRows,
    expectedNow,
    expectedPrev,
    expectedRange,
    expectedByMonth,
    accuracy,
    forecast,
    budget,
    verdict: buildVerdict({
      last30,
      paidShare,
      typical,
      valueRange,
      hasSubData: health.exportTransactions > 0 || health.revenueEvents > 0,
    }),
    rangeMarkers: markers.filter(m => m.date >= from && m.date <= today),
  };
}
